package DataBase

import Network.NetworkOutput
import Network.httpWorker
import Structs.Api.AiApproveAnswers
import Structs.Api.AiApproveQuestion
import Structs.Api.AiApproveResult
import Structs.Api.AiDraftConfig
import Structs.Api.AiDraftCreateRequest
import Structs.Api.AiDraftFull
import Structs.Api.AiDraftSummary
import Structs.Api.AiOrderConflict
import Structs.Api.AiSupplierProductCreateRequest
import Structs.Api.AiSupplierProductCreateResult
import Utils.getJsonWithConfig
import io.ktor.http.encodeURLParameter
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull

/**
 * Standalone client for the AI-assistant drafts API. Uses the JSON+bearer path
 * in [httpWorker] (`pureGetApi` / `purePostApi`) — NOT the ERP `post()` (which
 * is multipart + the ERP JWT). All methods are best-effort and return a typed
 * [AiResult]; callers map [AiResult.notConfigured] to an "AI not configured"
 * state and [AiResult.error] to a user message (incl. 409 conflicts).
 */
/**
 * Pure builders for PATCH /v1/drafts/{id} bodies. Kept in the shared module so
 * the Android app (which doesn't depend on kotlinx-serialization-json directly)
 * can produce overlay JSON without touching `JsonObject` itself. Each builder
 * shallow-merges onto the draft's existing `edited` overlay so unrelated edits
 * survive (the backend replaces the whole `edited` sub-key it receives).
 */
object AiDraftEdits {

    /** edited.meta.date + dateStrategy=custom (preserves the rest of meta). */
    fun dateBody(full: AiDraftFull, isoDate: String): String {
        val meta = (full.edited?.get("meta") as? JsonObject)?.toMutableMap() ?: mutableMapOf()
        meta["date"] = JsonPrimitive(isoDate)
        meta["dateStrategy"] = JsonPrimitive("custom")
        return JsonObject(mapOf("edited" to JsonObject(mapOf("meta" to JsonObject(meta))))).toString()
    }

    /** edited.products[lineId] merge (amount / unit / chosen product). */
    fun productBody(
        full: AiDraftFull,
        lineId: String,
        amount: Double?,
        unit: String?,
        productId: Int?,
        productName: String?,
        days: List<String>? = null,
        dayAvailable: Boolean? = null,
        /** The document unit price (supplier lines); null = unchanged. */
        price: Double? = null,
        /** The second (package) amount; 0 clears it, null = unchanged. */
        secondAmount: Double? = null,
        secondUnit: String? = null,
    ): String {
        val overlay = mutableMapOf<String, JsonElement>()
        (full.edited?.get("products") as? JsonObject)?.forEach { (k, v) -> overlay[k] = v }
        val line = (overlay[lineId] as? JsonObject)?.toMutableMap() ?: mutableMapOf()
        amount?.let { line["amount"] = JsonPrimitive(it) }
        unit?.takeIf { it.isNotBlank() }?.let { line["unit"] = JsonPrimitive(it) }
        price?.let { line["price"] = JsonPrimitive(it) }
        secondAmount?.let { line["secondAmount"] = JsonPrimitive(it) }
        secondUnit?.takeIf { it.isNotBlank() && (secondAmount ?: 0.0) > 0 }?.let { line["secondUnit"] = JsonPrimitive(it) }
        if (productId != null) {
            line["productId"] = JsonPrimitive(productId)
            line["productName"] = JsonPrimitive(productName)
            line["matchProductName"] = JsonPrimitive(productName)
            line["matchConfidence"] = JsonPrimitive(1.0)
            line["matchSource"] = JsonPrimitive("manual")
            if (days != null) {
                val arr = JsonArray(days.map { JsonPrimitive(it) })
                line["days"] = arr
                line["matchDays"] = arr
            }
            if (dayAvailable != null) {
                line["dayAvailable"] = JsonPrimitive(dayAvailable)
                line["matchDayAvailable"] = JsonPrimitive(dayAvailable)
            }
        }
        overlay[lineId] = JsonObject(line)
        return JsonObject(mapOf("edited" to JsonObject(mapOf("products" to JsonObject(overlay))))).toString()
    }

    /** Tombstone a line (`_deleted: true`) — same overlay contract as the web
     *  compare's removeRow. An overlay-only (user-added) line is removed
     *  outright instead, so it doesn't linger as a dead key. */
    fun deleteLineBody(full: AiDraftFull, lineId: String): String {
        val overlay = mutableMapOf<String, JsonElement>()
        (full.edited?.get("products") as? JsonObject)?.forEach { (k, v) -> overlay[k] = v }
        val existsInBase = full.resolvedProducts().any { it.lineId == lineId }
        if (existsInBase) {
            val line = (overlay[lineId] as? JsonObject)?.toMutableMap() ?: mutableMapOf()
            line["_deleted"] = JsonPrimitive(true)
            overlay[lineId] = JsonObject(line)
        } else {
            overlay.remove(lineId)
        }
        return JsonObject(mapOf("edited" to JsonObject(mapOf("products" to JsonObject(overlay))))).toString()
    }

    /** Manual client/supplier pick — backend pins the ERP id, then re-resolves. */
    fun entityBody(entityId: Int, entityName: String?): String =
        JsonObject(mapOf(
            "hint_entity_id" to JsonPrimitive(entityId.toString()),
            "hint_entity_name" to JsonPrimitive(entityName),
        )).toString()

    /** Backward-compatible name for older client-only callers. */
    fun clientBody(entityId: Int, entityName: String?): String = entityBody(entityId, entityName)

    fun docTypeBody(process: String): String =
        JsonObject(mapOf("process" to JsonPrimitive(process))).toString()
}

/** Filters accepted by GET /v1/drafts. */
data class AiDraftListOptions(
    val statuses: List<String> = emptyList(),
    val sources: List<String> = emptyList(),
    val process: String? = null,
    val entityRole: String? = null,
    val entityId: String? = null,
    val query: String? = null,
    val fromIso: String? = null,
    val toIso: String? = null,
    val limit: Int = 50,
    val offset: Int = 0,
    /** Open drafts the evening job parked after 3 days. */
    val archived: Boolean = false,
)

object AiDraftsClient {

    data class AiResult<T>(
        val data: T? = null,
        val ok: Boolean = false,
        val notConfigured: Boolean = false,
        /** Raw "<status>: <body>" or exception message on failure. */
        val error: String? = null,
        /** Populated when approve failed with a 409 order_conflict so the UI
         *  can offer create / overwrite / add-to-existing. */
        val orderConflict: AiOrderConflict? = null,
        /** True when approve failed because the doc was already loaded (duplicate). */
        val duplicate: Boolean = false,
        /** Populated when approve stopped on a question (missing price, units,
         *  supplier total, supplier prices) — answer it and approve again. */
        val approveQuestion: AiApproveQuestion? = null,
    )

    private fun base(cfg: AiDraftConfig) = cfg.apiBase.trimEnd('/')

    /** Compatibility overload for callers that only need a status filter. */
    suspend fun listDrafts(cfg: AiDraftConfig?, status: String?): AiResult<List<AiDraftSummary>> {
        val statuses = status
            ?.split(',')
            ?.map(String::trim)
            ?.filter(String::isNotEmpty)
            .orEmpty()
        return listDrafts(cfg, AiDraftListOptions(statuses = statuses))
    }

    /** GET /v1/drafts (summaries) with encoded server-side filters. */
    suspend fun listDrafts(
        cfg: AiDraftConfig?,
        options: AiDraftListOptions,
    ): AiResult<List<AiDraftSummary>> {
        if (cfg == null) return AiResult(notConfigured = true)
        val q = buildListQuery(options)
        val res = httpWorker.pureGetApi("${base(cfg)}/v1/drafts$q", token = cfg.token)
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return AiResult(error = res.first ?: "list failed")
        }
        return runCatching {
            val root = getJsonWithConfig().parseToJsonElement(res.first!!) as JsonObject
            val items = (root["items"] as? JsonArray) ?: JsonArray(emptyList())
            val list = items.mapNotNull {
                runCatching {
                    getJsonWithConfig().decodeFromJsonElement(AiDraftSummary.serializer(), it)
                }.getOrNull()
            }
            AiResult(data = list, ok = true)
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /** Public for platform-independent contract tests and legacy task adapters. */
    fun buildListQuery(options: AiDraftListOptions): String {
        val params = mutableListOf<Pair<String, String>>()
        fun add(name: String, value: String?) {
            value?.trim()?.takeIf(String::isNotEmpty)?.let { params += name to it }
        }
        val statuses = options.statuses.map(String::trim).filter(String::isNotEmpty).distinct()
        val sources = options.sources.map(String::trim).filter(String::isNotEmpty).distinct()
        if (statuses.isNotEmpty()) params += "status" to statuses.joinToString(",")
        if (sources.isNotEmpty()) params += "source" to sources.joinToString(",")
        add("process", options.process)
        add("entity_role", options.entityRole)
        add("entity_id", options.entityId)
        add("q", options.query)
        add("from", options.fromIso)
        add("to", options.toIso)
        if (options.archived) params += "archived" to "true"
        params += "limit" to options.limit.coerceIn(1, 500).toString()
        params += "offset" to options.offset.coerceAtLeast(0).toString()
        return params.joinToString(prefix = "?", separator = "&") { (name, value) ->
            "${name.encodeURLParameter()}=${value.encodeURLParameter()}"
        }
    }

    /** GET /v1/drafts/profiles/effective — the merged company/entity profile the
     *  pipeline actually applies. The mobile compare reads
     *  `defaults.entity_search_scope` ('ext'/'id'/'both') from it to decide
     *  whether rows show the external SKU (מק"ט) or the internal ERP id. */
    suspend fun getEffectiveProfile(
        cfg: AiDraftConfig?,
        entityRole: String,
        entityId: String? = null,
    ): AiResult<JsonObject> {
        if (cfg == null) return AiResult(notConfigured = true)
        val q = buildString {
            append("?entity_role=").append(entityRole.encodeURLParameter())
            entityId?.takeIf(String::isNotBlank)?.let {
                append("&entity_id=").append(it.encodeURLParameter())
            }
        }
        val res = httpWorker.pureGetApi(
            "${base(cfg)}/v1/drafts/profiles/effective$q", token = cfg.token,
        )
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return AiResult(error = res.first ?: "profile failed")
        }
        return runCatching {
            AiResult(
                data = getJsonWithConfig().parseToJsonElement(res.first!!) as JsonObject,
                ok = true,
            )
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /** Server-side hold for one long-poll leg (seconds). Kept under the typical
     *  25-30s mobile read timeout so a leg never fails just for waiting. */
    const val LONG_POLL_WAIT_SEC: Int = 20

    /** GET /v1/drafts/{id}.
     *
     *  [waitSec] > 0 turns the call into a long-poll: while the draft is still
     *  `extracting`/`resolving` the server holds the response until the status
     *  flips (or [waitSec] elapses) and returns the fresh row. Replaces the old
     *  fixed 2-3s polling cadence that added up to ~1.5s of dead time per stage
     *  plus one extra round-trip per poll. */
    suspend fun getDraft(cfg: AiDraftConfig?, id: String, waitSec: Int = 0): AiResult<AiDraftFull> {
        if (cfg == null) return AiResult(notConfigured = true)
        val q = if (waitSec > 0) "?wait=${waitSec.coerceIn(1, 30)}" else ""
        val res = httpWorker.pureGetApi("${base(cfg)}/v1/drafts/$id$q", token = cfg.token)
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return AiResult(error = res.first ?: "get failed")
        }
        return runCatching {
            AiResult(
                data = getJsonWithConfig().decodeFromString(AiDraftFull.serializer(), res.first!!),
                ok = true,
            )
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /** Statuses in which the pipeline is still running on the server. */
    fun isProcessing(status: String?): Boolean = status == "extracting" || status == "resolving"

    /**
     * Long-poll until the draft leaves `extracting`/`resolving`, or [maxLegs]
     * legs of [LONG_POLL_WAIT_SEC] have passed. [onUpdate] fires after every
     * leg that returned a row (including intermediate `resolving` ones, so a
     * screen can show extracted rows while matching is still running).
     *
     * Returns the last row seen (null only when every leg failed). Transient
     * network errors are retried with a short back-off; callers that need to
     * abort simply cancel the enclosing coroutine.
     */
    suspend fun awaitDraftReady(
        cfg: AiDraftConfig?,
        id: String,
        maxLegs: Int = 6,
        onUpdate: suspend (AiDraftFull) -> Unit = {},
    ): AiDraftFull? {
        if (cfg == null) return null
        var last: AiDraftFull? = null
        var failures = 0
        var legs = 0
        while (legs < maxLegs) {
            val r = getDraft(cfg, id, waitSec = LONG_POLL_WAIT_SEC)
            val row = r.data
            if (r.ok && row != null) {
                failures = 0
                last = row
                onUpdate(row)
                if (!isProcessing(row.status)) return row
                legs++
            } else {
                failures++
                if (failures >= 3) return last
                kotlinx.coroutines.delay(800L * failures)
            }
        }
        return last
    }

    /** GET /v1/drafts/pending-count — single COUNT for the "AI · N ממתינות"
     *  badge (drafts still extracting / resolving / ready). Replaces paging a
     *  500-row summary list just to count it. */
    suspend fun pendingCount(
        cfg: AiDraftConfig?,
        process: String? = null,
        entityRole: String? = null,
        excludeEntityId: String? = null,
    ): AiResult<Int> {
        if (cfg == null) return AiResult(notConfigured = true)
        val params = mutableListOf<String>()
        process?.takeIf(String::isNotBlank)?.let { params += "process=${it.encodeURLParameter()}" }
        entityRole?.takeIf(String::isNotBlank)?.let { params += "entity_role=${it.encodeURLParameter()}" }
        excludeEntityId?.takeIf(String::isNotBlank)
            ?.let { params += "exclude_entity_id=${it.encodeURLParameter()}" }
        val q = if (params.isEmpty()) "" else "?" + params.joinToString("&")
        val res = httpWorker.pureGetApi("${base(cfg)}/v1/drafts/pending-count$q", token = cfg.token)
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return AiResult(error = res.first ?: "count failed")
        }
        return runCatching {
            val root = getJsonWithConfig().parseToJsonElement(res.first!!) as JsonObject
            val n = (root["pending"] as? JsonPrimitive)?.contentOrNull?.toIntOrNull() ?: 0
            AiResult(data = n, ok = true)
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /** POST /v1/drafts/warm — pre-warm the server's ERP catalog / pricelist
     *  caches for the entity the user is about to paste an order for. Call it
     *  when the new-draft sheet opens; the response returns immediately and
     *  the resolve stage of the draft created seconds later is a cache hit.
     *  Best-effort: failures are swallowed. */
    suspend fun warm(
        cfg: AiDraftConfig?,
        entityRole: String? = null,
        entityId: String? = null,
        process: String? = null,
    ) {
        if (cfg == null) return
        val body = JsonObject(buildMap {
            entityRole?.takeIf(String::isNotBlank)?.let { put("entity_role", JsonPrimitive(it)) }
            entityId?.takeIf(String::isNotBlank)?.let { put("entity_id", JsonPrimitive(it)) }
            process?.takeIf(String::isNotBlank)?.let { put("process", JsonPrimitive(it)) }
        }).toString()
        runCatching {
            httpWorker.purePostApi(
                "${base(cfg)}/v1/drafts/warm", null,
                token = cfg.token, jsonStr = body, withContentHeader = "application/json",
            )
        }
    }

    /** POST /v1/drafts — create + auto-run extract/resolve. */
    suspend fun createDraft(cfg: AiDraftConfig?, req: AiDraftCreateRequest): AiResult<AiDraftFull> {
        if (cfg == null) return AiResult(notConfigured = true)
        val body = getJsonWithConfig().encodeToString(AiDraftCreateRequest.serializer(), req)
        val res = httpWorker.purePostApi(
            "${base(cfg)}/v1/drafts", null,
            token = cfg.token, jsonStr = body, withContentHeader = "application/json",
        )
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return AiResult(error = res.first ?: "create failed")
        }
        return runCatching {
            AiResult(
                data = getJsonWithConfig().decodeFromString(AiDraftFull.serializer(), res.first!!),
                ok = true,
            )
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /**
     * POST /v1/drafts/{id}/supplier-products — create or reuse a supplier-side
     * catalog row for one unmatched line, optionally linked to a client product.
     */
    suspend fun createSupplierProduct(
        cfg: AiDraftConfig?,
        id: String,
        req: AiSupplierProductCreateRequest,
    ): AiResult<AiSupplierProductCreateResult> {
        if (cfg == null) return AiResult(notConfigured = true)
        val body = getJsonWithConfig().encodeToString(AiSupplierProductCreateRequest.serializer(), req)
        val res = httpWorker.purePostApi(
            "${base(cfg)}/v1/drafts/$id/supplier-products", null,
            token = cfg.token, jsonStr = body, withContentHeader = "application/json",
        )
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return AiResult(error = res.first ?: "supplier product create failed")
        }
        return runCatching {
            AiResult(
                data = getJsonWithConfig().decodeFromString(
                    AiSupplierProductCreateResult.serializer(),
                    res.first!!,
                ),
                ok = true,
            )
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /** PATCH /v1/drafts/{id} — edit overlay / hints (date, client, products, process). */
    suspend fun patchDraft(cfg: AiDraftConfig?, id: String, jsonBody: String): AiResult<AiDraftFull> {
        if (cfg == null) return AiResult(notConfigured = true)
        val res = httpWorker.purePatchApi(
            "${base(cfg)}/v1/drafts/$id", token = cfg.token,
            jsonStr = jsonBody, withContentHeader = "application/json",
        )
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return AiResult(error = res.first ?: "patch failed")
        }
        return runCatching {
            AiResult(
                data = getJsonWithConfig().decodeFromString(AiDraftFull.serializer(), res.first!!),
                ok = true,
            )
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /** POST /v1/drafts/{id}/reresolve — re-match products (after entity/process change). */
    suspend fun reresolveDraft(cfg: AiDraftConfig?, id: String): AiResult<AiDraftFull> {
        if (cfg == null) return AiResult(notConfigured = true)
        val res = httpWorker.purePostApi(
            "${base(cfg)}/v1/drafts/$id/reresolve", null,
            token = cfg.token, jsonStr = "{}", withContentHeader = "application/json",
        )
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return AiResult(error = res.first ?: "reresolve failed")
        }
        return runCatching {
            AiResult(
                data = getJsonWithConfig().decodeFromString(AiDraftFull.serializer(), res.first!!),
                ok = true,
            )
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /** POST /v1/drafts/{id}/reject — the בטל on a draft card (nothing is created). */
    suspend fun rejectDraft(cfg: AiDraftConfig?, id: String, reason: String? = null): AiResult<AiDraftFull> {
        if (cfg == null) return AiResult(notConfigured = true)
        val body = JsonObject(mapOf("reason" to JsonPrimitive(reason))).toString()
        val res = httpWorker.purePostApi(
            "${base(cfg)}/v1/drafts/$id/reject", null,
            token = cfg.token, jsonStr = body, withContentHeader = "application/json",
        )
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return AiResult(error = res.first ?: "reject failed")
        }
        return runCatching {
            AiResult(
                data = getJsonWithConfig().decodeFromString(AiDraftFull.serializer(), res.first!!),
                ok = true,
            )
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /** POST /v1/drafts/{id}/undo — cancel a document the client trust created
     *  by itself (15-minute window, enforced by the server). */
    suspend fun undoDraft(cfg: AiDraftConfig?, id: String): AiResult<Boolean> {
        if (cfg == null) return AiResult(notConfigured = true)
        val res = httpWorker.purePostApi(
            "${base(cfg)}/v1/drafts/$id/undo", null,
            token = cfg.token, jsonStr = "{}", withContentHeader = "application/json",
        )
        if (res.second != NetworkOutput.SUCCESS) {
            return AiResult(error = res.first ?: "undo failed")
        }
        return AiResult(data = true, ok = true)
    }

    /** DELETE /v1/drafts/{id} — removes a draft (only allowed when not approved). */
    suspend fun deleteDraft(cfg: AiDraftConfig?, id: String): AiResult<Boolean> {
        if (cfg == null) return AiResult(notConfigured = true)
        val res = httpWorker.pureDeleteApi("${base(cfg)}/v1/drafts/$id", token = cfg.token)
        if (res.second != NetworkOutput.SUCCESS) {
            return AiResult(error = res.first ?: "delete failed")
        }
        return AiResult(data = true, ok = true)
    }

    /** POST /v1/drafts/{id}/approve — creates the real ERP document.
     *
     *  [resolveAction] resolves an order conflict: 'create' (new order),
     *  'update' (overwrite existing) or 'add' (append lines to existing).
     *  [resolveOrderId] is required for 'update'/'add'. A 409 order_conflict is
     *  parsed into [AiResult.orderConflict] so the UI can present the choice;
     *  the other questions into [AiResult.approveQuestion], answered through
     *  [answers]. */
    suspend fun approveDraft(
        cfg: AiDraftConfig?,
        id: String,
        learnConversionLines: List<String>? = null,
        force: Boolean = false,
        resolveAction: String? = null,
        resolveOrderId: Int? = null,
        answers: AiApproveAnswers? = null,
    ): AiResult<AiApproveResult> {
        if (cfg == null) return AiResult(notConfigured = true)
        val bodyObj = if (learnConversionLines != null) {
            val arr = learnConversionLines.joinToString(",") { "\"${it.replace("\"", "\\\"")}\"" }
            "{\"learn_conversion_lines\":[$arr]}"
        } else "{}"
        val params = mutableListOf<String>()
        if (force) params.add("force=true")
        if (!resolveAction.isNullOrBlank()) {
            params.add("resolve_action=$resolveAction")
            if (resolveOrderId != null) params.add("resolve_order_id=$resolveOrderId")
        }
        answers?.let { params.addAll(it.queryParams()) }
        val q = if (params.isEmpty()) "" else "?" + params.joinToString("&")
        val res = httpWorker.purePostApi(
            "${base(cfg)}/v1/drafts/$id/approve$q", null,
            token = cfg.token, jsonStr = bodyObj, withContentHeader = "application/json",
        )
        if (res.second != NetworkOutput.SUCCESS || res.first == null) {
            return parseApproveError(res.first)
        }
        return runCatching {
            AiResult(
                data = getJsonWithConfig().decodeFromString(AiApproveResult.serializer(), res.first!!),
                ok = true,
            )
        }.getOrElse { AiResult(error = it.message ?: "parse failed") }
    }

    /** Map a failed approve body ("<status>: <json>") into a typed result —
     *  pulling out an order_conflict candidate list or a duplicate flag. */
    private fun parseApproveError(raw: String?): AiResult<AiApproveResult> {
        val body = raw ?: return AiResult(error = "approve failed")
        val jsonStart = body.indexOf('{')
        if (jsonStart >= 0) {
            runCatching {
                val root = getJsonWithConfig().parseToJsonElement(body.substring(jsonStart)) as? JsonObject
                val detail = (root?.get("detail") as? JsonObject) ?: root
                val code = (detail?.get("error") as? JsonPrimitive)?.contentOrNull
                if (code == "order_conflict") {
                    val conflict = getJsonWithConfig()
                        .decodeFromJsonElement(AiOrderConflict.serializer(), detail!!)
                    return AiResult(error = body, orderConflict = conflict)
                }
                if (code == "duplicate_document") {
                    return AiResult(error = body, duplicate = true)
                }
                if (code in AiApproveQuestion.CODES) {
                    val question = getJsonWithConfig()
                        .decodeFromJsonElement(AiApproveQuestion.serializer(), detail!!)
                    return AiResult(error = question.message ?: body, approveQuestion = question)
                }
            }
        }
        return AiResult(error = body, duplicate = body.contains("duplicate") || body.contains("409"))
    }

    /** The question in a failed approve body, or null (exposed for tests). */
    fun approveQuestionOf(raw: String?): AiApproveQuestion? = parseApproveError(raw).approveQuestion
}
